import React from "react"
import clsx from "clsx"
import { css } from "styled-components"
import { BaseStyles, CustomSizeType } from "ui/types/base-styles"
import { generateStylesFromProps } from "ui/utils/styles"

import styles from "./styles.module.css"

export const common = css`
  box-sizing: border-box;
`

export const blockPaddingTop = (padding: string | CustomSizeType = "XL") => ({
  paddingTop:
    padding === "XL"
      ? "var(--space-xl)"
      : padding === "L"
        ? "var(--space-l)"
        : padding === "M"
          ? "var(--space-m)"
          : padding === "S"
            ? "var(--space-s)"
            : padding === "XS"
              ? "var(--space-xs)"
              : padding === "XXS"
                ? "var(--space-xxs)"
                : padding === "XXL"
                  ? "var(--space-xxl)"
                  : String(padding),
})

export const blockPaddingBottom = (padding: string | CustomSizeType = "XL") => ({
  paddingBottom:
    padding === "XL"
      ? "var(--space-xl)"
      : padding === "L"
        ? "var(--space-l)"
        : padding === "M"
          ? "var(--space-m)"
          : padding === "S"
            ? "var(--space-s)"
            : padding === "XS"
              ? "var(--space-xs)"
              : padding === "XXS"
                ? "var(--space-xxs)"
                : padding === "XXL"
                  ? "var(--space-xxl)"
                  : String(padding),
})

export const blockPadding = (padding: string | CustomSizeType = "XL") => ({
  ...blockPaddingTop(padding),
  ...blockPaddingBottom(padding),
})

export const blockStyles = (margin: string | CustomSizeType = "XL") => ({
  width: "100%",
  margin: `${
    margin === "XL"
      ? "var(--space-xl)"
      : margin === "L"
        ? "var(--space-l)"
        : margin === "M"
          ? "var(--space-m)"
          : margin === "S"
            ? "var(--space-s)"
            : margin === "XS"
              ? "var(--space-xs)"
              : margin === "XXS"
                ? "var(--space-xxs)"
                : margin === "XXL"
                  ? "var(--space-xxl)"
                  : String(margin)
  } auto`,
})

interface BaseComponentProps extends BaseStyles {
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
  onClick?: () => void
  dangerouslySetInnerHTML?: {
    __html: string
  }
}

export const Box = React.forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ children, className, style = {}, onClick, dangerouslySetInnerHTML, ...props }, ref) => {
    const generatedStyles = generateStylesFromProps(props)

    return (
      <div
        ref={ref}
        className={clsx(styles.common, styles.box, className)}
        style={{ ...generatedStyles, ...style }}
        onClick={onClick}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      >
        {!dangerouslySetInnerHTML && children}
      </div>
    )
  },
)

Box.displayName = "Box"

export const Column = React.forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ children, className, style = {}, onClick, $flexDirection = "column", ...props }, ref) => {
    const generatedStyles = generateStylesFromProps({ ...props, $flexDirection })

    return (
      <div
        ref={ref}
        className={clsx(styles.common, styles.column, className)}
        style={{ ...generatedStyles, ...style }}
        onClick={onClick}
      >
        {children}
      </div>
    )
  },
)

Column.displayName = "Column"

export const Row = React.forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ children, className, style = {}, onClick, ...props }, ref) => {
    const generatedStyles = generateStylesFromProps(props)

    return (
      <div
        ref={ref}
        className={clsx(styles.common, styles.row, className)}
        style={{ ...generatedStyles, ...style }}
        onClick={onClick}
      >
        {children}
      </div>
    )
  },
)

Row.displayName = "Row"

export const Grid = React.forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ children, className, style = {}, onClick, ...props }, ref) => {
    const generatedStyles = generateStylesFromProps(props)

    return (
      <div
        ref={ref}
        className={clsx(styles.common, styles.grid, className)}
        style={{ ...generatedStyles, ...style }}
        onClick={onClick}
      >
        {children}
      </div>
    )
  },
)

Grid.displayName = "Grid"

export const Absolute = React.forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ children, className, style = {}, onClick, ...props }, ref) => {
    const generatedStyles = generateStylesFromProps(props)

    return (
      <div
        ref={ref}
        className={clsx(styles.common, styles.box, styles.absolute, className)}
        style={{ ...generatedStyles, ...style }}
        onClick={onClick}
      >
        {children}
      </div>
    )
  },
)

Absolute.displayName = "Absolute"
